import { useMemo, useState } from 'react';
import { openAiProductDescriptionAPI } from '../../../context-api/api-services/OpenAi.services';

const productDescriptionAiCache = new Map();

export function useProductDescriptionAiDialog({
  setOpenProductDescripDialog,
  setDescription,
  title = '',
  productCategory = 'select',
  cuisineType = '',
}) {
  const hasCategory = productCategory && productCategory !== 'select';
  const hasCuisine = !!cuisineType && cuisineType !== 'select';

  const baseContext = useMemo(() => {
    const safeTitle = title?.trim() || 'your dish';
    const parts = [`menu item called "${safeTitle}"`];

    if (hasCategory) {
      parts.push(`in the "${productCategory}" category`);
    }
    if (hasCuisine) {
      parts.push(`from ${cuisineType} cuisine`);
    }

    return parts.join(', ');
  }, [title, hasCategory, hasCuisine, productCategory, cuisineType]);

  const predefinedPrompts = useMemo(
    () => [
      {
        id: 'default',
        label: 'Balanced & clear',
        text: `Write balanced, appetizing product descriptions for a ${baseContext}. Focus on taste, key ingredients, and what makes it special. Use 1–2 short sentences per description, friendly tone, no emojis.`,
      },
      {
        id: 'story',
        label: 'Story-driven',
        text: `Write warm, story-style product descriptions for a ${baseContext}. Make them feel homely and comforting, but still concise for a menu. Use 2–3 short sentences per description.`,
      },
      {
        id: 'punchy',
        label: 'Short & punchy',
        text: `Write short, punchy product descriptions for a ${baseContext}, ideal for delivery apps. Focus on cravings and key ingredients in 1–2 bold sentences.`,
      },
    ],
    [baseContext]
  );

  const [activePrompt, setActivePrompt] = useState(predefinedPrompts[0].text);
  const [chosenDescription, setChosenDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const chatResponseText = useMemo(() => {
    if (!suggestions.length) return '';
    return suggestions
      .map((s, index) => {
        const shortText = s?.short_description || '';
        const longText = s?.long_description || '';
        if (!shortText && !longText) return '';
        let block = `${index + 1}. ${shortText || longText}`;
        if (longText && shortText && longText !== shortText) {
          block += `\n   ${longText}`;
        }
        return block;
      })
      .filter(Boolean)
      .join('\n\n');
  }, [suggestions]);

  const chatSuggestions = useMemo(
    () => suggestions.map((s) => s.long_description || s.short_description || ''),
    [suggestions]
  );

  const handleClose = () => {
    setOpenProductDescripDialog(false);
  };

  const handleSelectPrompt = (promptText) => {
    setActivePrompt(promptText);
  };

  const handleGenerateDescriptions = async () => {
    if (!activePrompt?.trim()) return;
    const promptProductDescription = title?.trim() || 'your dish';

    const payload = {
      promptProductDescription,
      prompt: activePrompt,
      productCategory: hasCategory ? productCategory : 'main course',
      cuisine: hasCuisine ? cuisineType : 'indian',
    };

    const cacheKey = JSON.stringify(payload);

    // Check cache first – if we have a cached response for this
    // exact payload, reuse it instead of hitting the API.
    if (productDescriptionAiCache.has(cacheKey)) {
      const cachedSuggestions = productDescriptionAiCache.get(cacheKey) || [];
      setErrorMessage('');
      setSuggestions(cachedSuggestions);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuggestions([]);
    try {
      const response = await openAiProductDescriptionAPI(payload);
      if (response?.success === false || response?.error) {
        setErrorMessage(
          response?.error?.message || response?.data?.error || 'Something went wrong. Please try again.'
        );
        return;
      }
      if (response?.data?.success && Array.isArray(response.data.suggestions)) {
        const nextSuggestions = response.data.suggestions.slice(0, 5);
        // Save to cache for this exact payload so the next
        // identical request is served from memory.
        productDescriptionAiCache.set(cacheKey, nextSuggestions);
        setSuggestions(nextSuggestions);
      } else if (response?.data && response.data.success === false) {
        setErrorMessage(response.data.error || 'Could not get suggestions. Please try again.');
      } else {
        setErrorMessage('Could not get suggestions. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChooseSuggestion = (value) => {
    setChosenDescription(value);
  };

  const handleSelectDescription = () => {
    if (!chosenDescription?.trim()) return;
    setDescription(chosenDescription.trim());
    setOpenProductDescripDialog(false);
  };

  return {
    hasCategory,
    hasCuisine,
    predefinedPrompts,
    activePrompt,
    setActivePrompt,
    chosenDescription,
    isLoading,
    suggestions,
    errorMessage,
    chatResponseText,
    chatSuggestions,
    handleClose,
    handleSelectPrompt,
    handleGenerateDescriptions,
    handleChooseSuggestion,
    handleSelectDescription,
  };
}
